// ============================================================
// SecaoPlanejamento.js — o plano trimestral de quem executa
//
// Mesma tela para o técnico, o auxiliar e o preparador físico. O que muda é
// o TIPO do plano e o recorte, e os dois vêm do servidor: o técnico recebe
// só o plano técnico da modalidade dele, o preparador recebe os físicos.
// Duas telas quase iguais divergiriam na primeira alteração.
//
// O rascunho salva sozinho enquanto a pessoa digita. Plano trimestral é
// texto longo, escrito em pedaços, e perder o que foi digitado por causa de
// uma aba fechada é o tipo de coisa que faz alguém desistir da ferramenta.
// Por isso a tela também DIZ que salvou, com a hora: promessa de salvamento
// automático sem confirmação visível leva a pessoa a reescrever por medo.
// ============================================================

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { listarPlanejamento, salvarPlano, enviarPlano } from "../firebase";
import { gerarPDFPlano } from "../pdfRelatorio";
import { useNomeNucleo } from "../NucleosContexto";
import {
  MESES, AZUL, AZUL_ESCURO, AZUL_CLARO, TEXTO, CINZA_ESC, BRANCO, PERIGO,
  OK, ATENCAO, ATENCAO_CL, RUIM, RUIM_CL, OK_CL, BORDA, FUNDO_SUAVE,
} from "../tema";
import { Card, Btn, StatusBox } from "../componentes";

// Faixa dos sinais diacríticos, montada por código de caractere: escrita
// como literal, ela vira um acento invisível e some no primeiro editor que
// normalizar o arquivo.
const DIACRITICOS = new RegExp(
  "[" + String.fromCharCode(0x300) + "-" + String.fromCharCode(0x36f) + "]",
  "g"
);
const ESPERA_PARA_SALVAR = 1200;

// Microciclo: a semana que se repete dentro do mês.
//
// Cinco dias e não sete, porque o programa treina de segunda a sexta.
// Sábado e domingo seriam dois campos sempre vazios, e campo sempre vazio
// ensina quem preenche a pular campo.
const DIAS_DO_MICROCICLO = [
  { chave: "seg", rotulo: "Segunda" },
  { chave: "ter", rotulo: "Terça" },
  { chave: "qua", rotulo: "Quarta" },
  { chave: "qui", rotulo: "Quinta" },
  { chave: "sex", rotulo: "Sexta" },
];

// Até cinco semanas por mês. A regra é a mesma do servidor (planos.js), e
// está repetida aqui de propósito: o botão precisa saber quando parar antes
// de o servidor recusar.
const MAXIMO_DE_SEMANAS = 5;

// As semanas de um mês. Plano gravado antes desta mudança tem `microciclo`,
// um objeto só, e ele continua sendo a primeira semana: a leitura converte,
// e nada precisa ser migrado no banco.
function semanasDoMes(m) {
  const mes = m || {};
  if (Array.isArray(mes.microciclos) && mes.microciclos.length) {
    return mes.microciclos.slice(0, MAXIMO_DE_SEMANAS);
  }
  return [mes.microciclo || {}];
}

function diasEscritos(semana) {
  return Object.values(semana || {}).filter((v) => String(v || "").trim()).length;
}

// "2026-09-28T19:43:02Z" vira "28/09 às 16:43". Data e hora, porque o plano
// é retomado no mesmo dia mais de uma vez e só a data não diz qual versão
// está na tela.
function dataHoraCurta(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso || "");
  const dia = String(d.getDate()).padStart(2, "0");
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  return `${dia}/${mes} às ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
}

function fmt(iso) {
  if (!iso) return "—";
  const p = String(iso).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
}

// Nome de cada um dos três meses do trimestre.
//
// "Mês 1, Mês 2, Mês 3" obriga quem preenche a traduzir mentalmente para o
// calendário dele. O trimestre tem data de início, então o nome sai de
// graça: outubro, novembro, dezembro.
function nomesDosMeses(dataInicio) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dataInicio || ""))) {
    return ["Mês 1", "Mês 2", "Mês 3"];
  }
  const [ano, mes] = dataInicio.split("-").map(Number);
  return [0, 1, 2].map((i) => {
    const total = (mes - 1) + i;
    const m = total % 12;
    const a = ano + Math.floor(total / 12);
    return a === ano ? MESES[m] : `${MESES[m]} de ${a}`;
  });
}

// "10 a 15/03/2026", "14/02/2026", "28/02 a 03/03/2026".
// Mesma regra do servidor, porque a tela precisa mostrar antes de salvar.
function periodoDaCompeticao(c) {
  const ok = (d) => /^\d{4}-\d{2}-\d{2}$/.test(String(d || ""));
  if (!ok(c.dataInicio)) return "";
  const dia = (i) => i.slice(8, 10);
  const mesAno = (i) => `${i.slice(5, 7)}/${i.slice(0, 4)}`;
  if (!ok(c.dataFim) || c.dataFim === c.dataInicio) return `${dia(c.dataInicio)}/${mesAno(c.dataInicio)}`;
  if (c.dataInicio.slice(0, 7) === c.dataFim.slice(0, 7)) {
    return `${dia(c.dataInicio)} a ${dia(c.dataFim)}/${mesAno(c.dataInicio)}`;
  }
  return `${dia(c.dataInicio)}/${c.dataInicio.slice(5, 7)} a ${dia(c.dataFim)}/${mesAno(c.dataFim)}`;
}

// O que ainda impede o envio.
//
// Mesma régua do servidor, em validarEnvio. Está repetida aqui de propósito:
// quem preenche precisa saber o que falta ANTES de clicar, e não descobrir
// pela recusa. A do servidor continua sendo a que vale.
function oQueFalta(conteudo, { cobrarCompeticoes = true, cobrarMicrociclo = true, nomes = null } = {}) {
  const faltas = [];
  const c = conteudo || {};

  if (!String(c.objetivoGeral || "").trim()) faltas.push("o objetivo geral do trimestre");

  // Os três meses, e não "pelo menos um": o plano é do trimestre inteiro, e
  // um mês só entregue como plano de trimestre deixa os outros dois para um
  // depois que não chega.
  const meses = Array.isArray(c.meses) ? c.meses : [];
  for (let i = 0; i < 3; i += 1) {
    const m = meses[i] || {};
    const nome = (nomes && nomes[i]) || `mês ${i + 1}`;
    if (!String(m.foco || "").trim()) faltas.push(`o objetivo de ${nome}`);
    if (cobrarMicrociclo) {
      const semanas = semanasDoMes(m);
      if (!diasEscritos(semanas[0])) faltas.push(`o microciclo de ${nome}`);
      semanas.slice(1).forEach((semana, j) => {
        if (!diasEscritos(semana)) faltas.push(`a semana ${j + 2} de ${nome}`);
      });
    }
  }

  // Competição não é cobrada de quem não cadastra competição.
  (cobrarCompeticoes ? (Array.isArray(c.competicoes) ? c.competicoes : []) : []).forEach((comp, i) => {
    const n = i + 1;
    if (!String(comp.nome || "").trim()) faltas.push(`o nome da competição ${n}`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(comp.dataInicio || ""))) {
      faltas.push(`a data da competição ${n}`);
    } else if (comp.dataFim && comp.dataFim < comp.dataInicio) {
      faltas.push(`a data de fim da competição ${n}, que está antes do início`);
    }
  });

  return faltas;
}

const ESTADO = {
  rascunho: { txt: "Rascunho", bg: "#F1F5F9", cor: CINZA_ESC },
  enviado: { txt: "Enviado, em análise", bg: "#DBEAFE", cor: "#1E40AF" },
  aprovado: { txt: "✓ Aprovado", bg: OK_CL, cor: OK },
  devolvido: { txt: "Devolvido para ajuste", bg: RUIM_CL, cor: "#991B1B" },
};

function Etiqueta({ status }) {
  const e = ESTADO[status] || ESTADO.rascunho;
  return (
    <span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: e.bg, color: e.cor, whiteSpace: "nowrap" }}>
      {e.txt}
    </span>
  );
}

// Seção numerada.
//
// O plano tem cinco blocos e a pessoa preenche em pedaços, ao longo de
// dias. O número dá ordem à leitura e serve de referência na conversa com a
// coordenação: "falta o 3" é mais curto que descrever o bloco.
function Secao({ numero, titulo, sub, acao, children }) {
  return (
    <Card>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 11, alignItems: "flex-start", minWidth: 0 }}>
          <span style={{
            width: 24, height: 24, borderRadius: "50%", background: AZUL, color: BRANCO,
            fontSize: 12.5, fontWeight: 800, display: "flex", alignItems: "center",
            justifyContent: "center", flexShrink: 0, marginTop: 1,
          }}>{numero}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15.5, fontWeight: 800, color: TEXTO }}>{titulo}</div>
            {sub && (
              <div style={{ fontSize: 12.5, color: CINZA_ESC, marginTop: 3, lineHeight: 1.55 }}>{sub}</div>
            )}
          </div>
        </div>
        {acao}
      </div>
      {children}
    </Card>
  );
}

// Em que pé está o mês, para a sanfona dizer sem precisar abrir.
//
// "Não preenchido" e "em preenchimento" são estados diferentes de propósito:
// o primeiro é trabalho que nem começou, o segundo é trabalho pela metade, e
// quem olha a lista precisa saber por onde continuar.
function situacaoDoMes(m) {
  const foco = String((m && m.foco) || "").trim();
  const conteudos = String((m && m.conteudos) || "").trim();
  const semanas = semanasDoMes(m);
  const dias = semanas.reduce((n, semana) => n + diasEscritos(semana), 0);
  // TODAS as semanas, e não a soma: um mês com cinco semanas e quatro
  // vazias somava dias e se dizia preenchido, que é exatamente o caso que
  // o selo existe para denunciar.
  const todasEscritas = semanas.every((semana) => diasEscritos(semana));

  if (!foco && !conteudos && !dias) {
    return { txt: "Não preenchido", bg: "#F1F5F9", cor: CINZA_ESC };
  }
  if (foco && conteudos && todasEscritas) {
    return { txt: "Preenchido", bg: OK_CL, cor: OK };
  }
  return { txt: "Em preenchimento", bg: ATENCAO_CL, cor: ATENCAO };
}

function Rotulo({ children, cor }) {
  return (
    <div style={{ fontSize: 11, color: cor || CINZA_ESC, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 5 }}>
      {children}
    </div>
  );
}

const estiloCampo = {
  width: "100%", border: "1px solid #D1D5DB", borderRadius: 8,
  padding: "9px 11px", fontSize: 13.5, fontFamily: "inherit",
  boxSizing: "border-box", color: TEXTO, background: BRANCO,
};

// Campo de texto que cresce com o que se escreve.
//
// O dia de um microciclo pode ser uma palavra ou um parágrafo, e caixa de
// altura fixa obriga a rolar dentro do campo para reler o que se acabou de
// escrever. Cresce até um teto e só então rola, senão um dia muito detalhado
// empurraria os outros quatro para fora da tela.
function CampoQueCresce({ value, onChange, disabled, placeholder, minimoDeLinhas = 3, style = {} }) {
  const ref = useRef(null);

  const ajustar = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    // Ambiente sem layout devolve scrollHeight zero, e escrever "0px" aqui
    // achataria o campo até sumir. Nesse caso a altura das linhas continua
    // valendo, que é o certo.
    const medida = el.scrollHeight;
    if (!medida) return;
    const teto = 320;
    el.style.height = Math.min(medida, teto) + "px";
    el.style.overflowY = medida > teto ? "auto" : "hidden";
  }, []);

  // Ajusta também quando o valor chega de fora, como na cópia de um mês para
  // outro: sem isto, o campo copiado ficaria com a altura antiga.
  useEffect(() => { ajustar(); }, [value, ajustar]);

  return (
    <textarea
      ref={ref}
      rows={minimoDeLinhas}
      disabled={disabled}
      value={value}
      placeholder={placeholder}
      onChange={(e) => { onChange(e.target.value); ajustar(); }}
      style={{ ...estiloCampo, resize: "vertical", overflowY: "hidden", ...style }}
    />
  );
}

// Faixa do prazo.
//
// O prazo estava como texto pequeno no cabeçalho, do mesmo tamanho do resto.
// Prazo que não pressiona não é prazo: aqui ele tem cor, conta os dias e
// muda de tom quando aperta.
function FaixaDoPrazo({ prazo, hojeISO, status }) {
  if (!prazo) return null;
  if (status === "aprovado" || status === "enviado") return null;

  const dias = (() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(hojeISO || ""))) return null;
    const [a1, m1, d1] = hojeISO.split("-").map(Number);
    const [a2, m2, d2] = prazo.split("-").map(Number);
    return Math.round((Date.UTC(a2, m2 - 1, d2) - Date.UTC(a1, m1 - 1, d1)) / 86400000);
  })();
  if (dias === null) return null;

  const atrasado = dias < 0;
  const apertado = dias >= 0 && dias <= 3;
  const cor = atrasado ? RUIM : apertado ? ATENCAO : AZUL_ESCURO;
  const fundo = atrasado ? RUIM_CL : apertado ? ATENCAO_CL : AZUL_CLARO;

  const texto = atrasado
    ? `Prazo vencido há ${-dias} dia(s). Entregue assim que puder.`
    : dias === 0
      ? "Último dia para entregar o plano."
      : `Faltam ${dias} dia(s) para entregar o plano.`;

  return (
    <div style={{
      background: fundo, border: `1.5px solid ${cor}33`, borderRadius: 10,
      padding: "11px 15px", display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap",
    }}>
      <span style={{ fontSize: 13.5, fontWeight: 800, color: cor }}>{texto}</span>
      <span style={{ fontSize: 12.5, color: cor, opacity: 0.85 }}>
        Prazo: {fmt(prazo)}
      </span>
    </div>
  );
}

export default function SecaoPlanejamento({ userNucleo }) {
  const nomeNucleo = useNomeNucleo();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [status, setStatus] = useState(null);
  const [modalidadeAtiva, setModalidadeAtiva] = useState("");
  const [trimestreEscolhido, setTrimestreEscolhido] = useState("");
  const [mesAberto, setMesAberto] = useState(0);
  const [conteudo, setConteudo] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [salvoEm, setSalvoEm] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [baixando, setBaixando] = useState(false);
  // O pedido que ainda não subiu, com o contexto em que foi digitado.
  const pendente = useRef(null);
  const relogio = useRef(null);
  const sujo = useRef(false);

  const recarregar = useCallback(async () => {
    setErro(null);
    try {
      const r = await listarPlanejamento(userNucleo, trimestreEscolhido || undefined);
      setDados(r);
    } catch (e) {
      setErro("Não foi possível carregar: " + (e.message || ""));
      setDados({ trimestres: [], planos: [], modalidades: [] });
    }
  }, [userNucleo, trimestreEscolhido]);

  useEffect(() => { recarregar(); }, [recarregar]);

  // Escolha inicial da modalidade, separada do carregamento. Junto, a escolha
  // recriava a função de carregar, que disparava o efeito, que carregava de
  // novo: laço.
  useEffect(() => {
    if (!dados || modalidadeAtiva) return;
    setModalidadeAtiva(dados.papel === "fisico"
      ? (dados.modalidades || [])[0] || ""
      : dados.minhaModalidade || "");
  }, [dados, modalidadeAtiva]);

  const trimestre = useMemo(() => {
    if (!dados || !dados.trimestres) return null;
    const escolhido = trimestreEscolhido || dados.trimestreAtivoId;
    return dados.trimestres.find((t) => t.id === escolhido) || dados.trimestres[0] || null;
  }, [dados, trimestreEscolhido]);

  const tipo = dados && dados.papel === "fisico" ? "fisico" : "tecnico";

  const plano = useMemo(() => {
    if (!dados || !modalidadeAtiva) return null;
    const chave = (m) => String(m || "").normalize("NFD").replace(DIACRITICOS, "").toLowerCase().replace(/[^a-z0-9]/g, "");
    return (dados.planos || []).find((p) => p.tipo === tipo && chave(p.modalidade) === chave(modalidadeAtiva)) || null;
  }, [dados, modalidadeAtiva, tipo]);

  // O conteúdo em edição nasce do que veio do servidor, e é trocado quando
  // muda a modalidade. Sem esta linha, o preparador levaria o texto de uma
  // modalidade para a outra ao alternar entre elas.
  useEffect(() => {
    const vazio = {
      objetivoGeral: "",
      meses: [{}, {}, {}].map(() => ({ foco: "", conteudos: "", microciclos: [{}] })),
      volumeSemanal: "", objetivosPorAtleta: [], avaliacaoConteudo: "",
      competicoes: [], observacoes: "",
    };
    const doServidor = plano && plano.conteudo ? { ...vazio, ...plano.conteudo } : vazio;
    setConteudo({
      ...doServidor,
      meses: (doServidor.meses || []).map((m) => ({ ...m, microciclos: semanasDoMes(m) })),
    });
    setSalvoEm(null);
    sujo.current = false;
  }, [plano, modalidadeAtiva]);

  const bloqueado = plano && (plano.status === "enviado" || plano.status === "aprovado");

  // Trimestre passado é só leitura, mesmo em rascunho. Voltar para consultar
  // o que foi feito é útil; reabrir um plano antigo por engano, não.
  const ehTrimestreAnterior = Boolean(
    dados && trimestre && dados.trimestreAtivoId && trimestre.id !== dados.trimestreAtivoId
  );
  const somenteLeitura = bloqueado || ehTrimestreAnterior;

  // Salva sozinho, um pouco depois da última tecla. Sem espera, cada letra
  // viraria uma gravação; com espera longa demais, perde-se o que foi
  // digitado ao fechar a aba.
  const gravar = useCallback(async (pedido) => {
    if (!pedido) return;
    setSalvando(true);
    try {
      await salvarPlano(pedido);
      if (pendente.current === pedido) { pendente.current = null; sujo.current = false; }
      setSalvoEm(new Date());
    } catch (e) {
      setStatus({ tipo: "erro", msg: "Não foi possível salvar: " + (e.message || "") });
    } finally {
      setSalvando(false);
    }
  }, []);

  const agendarSalvamento = useCallback((novo) => {
    if (!trimestre || !modalidadeAtiva || somenteLeitura) return;
    sujo.current = true;
    pendente.current = { trimestreId: trimestre.id, tipo, modalidade: modalidadeAtiva, conteudo: novo };
    if (relogio.current) clearTimeout(relogio.current);
    relogio.current = setTimeout(() => gravar(pendente.current), ESPERA_PARA_SALVAR);
  }, [trimestre, modalidadeAtiva, tipo, somenteLeitura, gravar]);

  // Salvar agora. É o botão, e é o que garante que sair da tela no meio do
  // preenchimento não custa o último parágrafo digitado: o plano é escrito
  // em pedaços, ao longo de dias, e a pausa de digitação de 1,2 segundo
  // não cobre quem escreve e fecha a aba.
  const salvarAgora = useCallback(async () => {
    if (relogio.current) { clearTimeout(relogio.current); relogio.current = null; }
    if (pendente.current) await gravar(pendente.current);
  }, [gravar]);

  // Ao sair da tela ou trocar de plano, o pendente é GRAVADO, e não
  // descartado. Antes o clearTimeout da limpeza jogava fora o que ainda não
  // tinha subido.
  useEffect(() => () => {
    if (relogio.current) { clearTimeout(relogio.current); relogio.current = null; }
    if (pendente.current) {
      const pedido = pendente.current;
      pendente.current = null;
      salvarPlano(pedido).catch(() => {});
    }
  }, [trimestre, modalidadeAtiva, tipo]);

  function mudar(campo, valor) {
    setConteudo((atual) => {
      const novo = { ...atual, [campo]: valor };
      agendarSalvamento(novo);
      return novo;
    });
  }

  function mudarMes(i, campo, valor) {
    setConteudo((atual) => {
      const meses = atual.meses.map((m, k) => (k === i ? { ...m, [campo]: valor } : m));
      const novo = { ...atual, meses };
      agendarSalvamento(novo);
      return novo;
    });
  }

  function mudarDiaDoMicrociclo(i, semana, dia, valor) {
    setConteudo((atual) => {
      const meses = atual.meses.map((m, k) => {
        if (k !== i) return m;
        const semanas = semanasDoMes(m).map((sem, j) => (
          j === semana ? { ...sem, [dia]: valor } : sem
        ));
        return { ...m, microciclos: semanas };
      });
      const novo = { ...atual, meses };
      agendarSalvamento(novo);
      return novo;
    });
  }

  // Uma semana a mais, até cinco.
  //
  // A semana nasce vazia e não copiada da anterior: quem acrescenta semana
  // é justamente quem tem uma diferente para escrever, e um campo já
  // preenchido com o texto de outra semana seria apagado antes de servir.
  function adicionarSemana(i) {
    setConteudo((atual) => {
      const meses = atual.meses.map((m, k) => {
        if (k !== i) return m;
        const semanas = semanasDoMes(m);
        if (semanas.length >= MAXIMO_DE_SEMANAS) return m;
        return { ...m, microciclos: [...semanas, {}] };
      });
      const novo = { ...atual, meses };
      agendarSalvamento(novo);
      return novo;
    });
  }

  // Remover só pergunta quando há o que perder. Confirmação em campo vazio
  // é a que ensina a clicar em "sim" sem ler.
  function removerSemana(i, semana) {
    const atualMes = conteudo.meses[i];
    const alvo = semanasDoMes(atualMes)[semana];
    if (diasEscritos(alvo) && !window.confirm(
      `Remover a semana ${semana + 1}?\n\nO que estiver escrito nela será perdido.`
    )) return;

    setConteudo((atual) => {
      const meses = atual.meses.map((m, k) => (
        k === i ? { ...m, microciclos: semanasDoMes(m).filter((_, j) => j !== semana) } : m
      ));
      const novo = { ...atual, meses };
      agendarSalvamento(novo);
      return novo;
    });
  }

  // Copiar um mês para outro.
  //
  // O microciclo do mês seguinte costuma nascer do anterior com poucos
  // ajustes, e redigitar tudo é o tipo de trabalho que faz a pessoa
  // simplificar o plano para não ter que escrever de novo.
  function copiarMesPara(origem, destino) {
    if (!window.confirm(
      "Copiar o conteúdo deste mês para o outro?\n\nO que estiver lá será substituído."
    )) return;

    setConteudo((atual) => {
      const de = atual.meses[origem] || {};
      const meses = atual.meses.map((m, k) => (k === destino
        ? {
          ...m,
          foco: de.foco || "",
          conteudos: de.conteudos || "",
          microciclos: semanasDoMes(de).map((sem) => ({ ...sem })),
        }
        : m));
      const novo = { ...atual, meses };
      agendarSalvamento(novo);
      return novo;
    });
    setMesAberto(destino);
  }

  function mudarLista(campo, i, patch) {
    setConteudo((atual) => {
      const lista = (atual[campo] || []).map((item, k) => (k === i ? { ...item, ...patch } : item));
      const novo = { ...atual, [campo]: lista };
      agendarSalvamento(novo);
      return novo;
    });
  }

  function adicionar(campo, item) {
    setConteudo((atual) => {
      const novo = { ...atual, [campo]: [...(atual[campo] || []), item] };
      agendarSalvamento(novo);
      return novo;
    });
  }

  function remover(campo, i) {
    setConteudo((atual) => {
      const novo = { ...atual, [campo]: (atual[campo] || []).filter((_, k) => k !== i) };
      agendarSalvamento(novo);
      return novo;
    });
  }

  // O microciclo é cobrado do plano técnico e não do físico: na preparação
  // física os dias se alternam conforme o treino de pista, e cobrar ali
  // produziria texto inventado para vencer a trava.
  const faltas = useMemo(
    () => oQueFalta(conteudo, {
      cobrarCompeticoes: tipo !== "fisico",
      cobrarMicrociclo: tipo !== "fisico",
      nomes: trimestre ? nomesDosMeses(trimestre.dataInicio).map((n) => n.toLowerCase()) : null,
    }),
    [conteudo, tipo, trimestre]
  );

  async function enviar() {
    if (!trimestre) return;
    if (faltas.length) {
      setStatus({
        tipo: "erro",
        msg: "Antes de enviar, preencha: " + faltas.join("; ") + ".",
      });
      return;
    }
    if (!window.confirm(
      "Enviar o plano para a coordenação?\n\nDepois do envio ele fica somente leitura até a coordenação aprovar ou devolver."
    )) return;

    setEnviando(true);
    setStatus(null);
    try {
      if (relogio.current) clearTimeout(relogio.current);
      await enviarPlano({ trimestreId: trimestre.id, tipo, modalidade: modalidadeAtiva, conteudo });
      setStatus({ tipo: "ok", msg: "Plano enviado para a coordenação." });
      await recarregar();
    } catch (e) {
      setStatus({ tipo: "erro", msg: e.message || "Não foi possível enviar." });
    } finally {
      setEnviando(false);
    }
  }

  async function baixarPDF() {
    setBaixando(true);
    try {
      await gerarPDFPlano({
        plano: { ...(plano || {}), conteudo, tipo, modalidade: modalidadeAtiva, status: plano ? plano.status : "rascunho" },
        trimestre,
        nucleo: userNucleo,
        nucleoNome: nomeNucleo(userNucleo),
      });
    } catch (e) {
      setStatus({ tipo: "erro", msg: "Não foi possível gerar o PDF: " + (e.message || "") });
    } finally {
      setBaixando(false);
    }
  }

  // conteudo nasce null e só é preenchido num efeito. Renderizar os campos
  // antes disso quebraria a tela em "conteudo.objetivoGeral" de undefined.
  if (!dados || !conteudo) {
    return <Card><div style={{ fontSize: 13, color: CINZA_ESC }}>Carregando…</div></Card>;
  }

  if (!trimestre) {
    return (
      <Card>
        <div style={{ fontWeight: 700, fontSize: 15.5 }}>Nenhum trimestre aberto</div>
        <div style={{ fontSize: 13, color: CINZA_ESC, marginTop: 6, lineHeight: 1.6 }}>
          A coordenação ainda não abriu o trimestre. Quando abrir, o plano aparece aqui com o
          prazo de entrega.
        </div>
      </Card>
    );
  }

  const rotuloTipo = tipo === "fisico" ? "físico" : "técnico";
  const avaliacaoDeQuem = tipo === "fisico"
    ? "A avaliação e os testes físicos são responsabilidade do preparador físico e do auxiliar."
    : "A avaliação técnica é responsabilidade do técnico e do auxiliar.";
  const meses = nomesDosMeses(trimestre.dataInicio);

  // O preparador atende todas as modalidades, e o servidor manda os atletas
  // de todas elas. A lista tem que ser a da modalidade ABERTA: oferecer a
  // turma inteira do núcleo faria o objetivo do nadador aparecer no plano do
  // BMX, e ninguém percebe isso relendo depois.
  const daModalidade = (m) => !modalidadeAtiva
    || String(m || "").normalize("NFD").replace(DIACRITICOS, "").toLowerCase().replace(/[^a-z0-9]/g, "")
      === String(modalidadeAtiva).normalize("NFD").replace(DIACRITICOS, "").toLowerCase().replace(/[^a-z0-9]/g, "");

  const atletas = (dados.atletas || []).filter((a) => daModalidade(a.modalidade));

  // As competições que o técnico da modalidade cadastrou. Chave pela grafia
  // do cadastro, com o mesmo normalizador do resto, porque "Tênis de Mesa" e
  // "Tenis de Mesa" convivem no sistema.
  const competicoesDoTecnico = (() => {
    const mapa = dados.competicoesPorModalidade || {};
    const achada = Object.keys(mapa).find((m) => daModalidade(m));
    return achada ? mapa[achada] : [];
  })();

  const categorias = [...new Set(atletas.map((a) => a.categoria).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, "pt-BR"));

  // Atleta já usado não aparece de novo na lista do próximo: dois objetivos
  // para a mesma pessoa no mesmo trimestre é sempre engano.
  const jaEscolhidos = new Set((conteudo.objetivosPorAtleta || []).map((o) => o.atletaUid).filter(Boolean));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {erro && <StatusBox tipo="erro" msg={erro} />}
      {status && <StatusBox tipo={status.tipo} msg={status.msg} />}

      {plano && plano.status === "devolvido" && plano.observacoesCoordenacao && (
        <div style={{ background: RUIM_CL, border: "1px solid #FECACA", borderRadius: 10, padding: "13px 16px" }}>
          <div style={{ fontWeight: 700, fontSize: 13.5, color: "#991B1B" }}>Devolvido pela coordenação</div>
          <div style={{ fontSize: 13, color: "#991B1B", marginTop: 4, lineHeight: 1.6, whiteSpace: "pre-line" }}>
            {plano.observacoesCoordenacao}
          </div>
        </div>
      )}

      {!ehTrimestreAnterior && (
        <FaixaDoPrazo
          prazo={trimestre.prazoEnvio}
          hojeISO={dados.hojeISO}
          status={plano ? plano.status : "rascunho"}
        />
      )}

      {/* 1. Identificação. Não é campo de preencher, é o contexto de tudo que
          vem abaixo: de que plano se trata, de quem, e até quando. */}
      <Secao
        numero={1}
        titulo="Identificação"
        sub={`${trimestre.nome} · plano ${rotuloTipo}${modalidadeAtiva ? ` · ${modalidadeAtiva}` : ""} · ${fmt(trimestre.dataInicio)} a ${fmt(trimestre.dataFim)} · prazo de entrega ${fmt(trimestre.prazoEnvio)}`}
        acao={
          <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
            <Etiqueta status={plano ? plano.status : "rascunho"} />
            <button onClick={baixarPDF} disabled={baixando} style={{
              background: "none", border: `1px solid ${BORDA}`, borderRadius: 8,
              padding: "5px 11px", fontSize: 12, fontWeight: 600, color: TEXTO,
              cursor: "pointer", fontFamily: "inherit",
            }}>
              {baixando ? "…" : "📄 Baixar plano"}
            </button>
          </div>
        }
      >
        {(dados.trimestres || []).length > 1 && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 11.5, color: CINZA_ESC, fontWeight: 700 }}>Trimestre</span>
            <select value={trimestre.id} onChange={(e) => setTrimestreEscolhido(e.target.value)}
              style={{ ...estiloCampo, width: "auto", minWidth: 190, cursor: "pointer" }}>
              {(dados.trimestres || []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome}{t.id === dados.trimestreAtivoId ? " (atual)" : ""}
                </option>
              ))}
            </select>
            {ehTrimestreAnterior && (
              <span style={{ fontSize: 12, color: ATENCAO, fontWeight: 700 }}>
                Trimestre anterior, somente leitura
              </span>
            )}
          </div>
        )}

        {/* Uma aba por modalidade, com o estado do plano de cada uma. São
            vários planos por trimestre, e sem a marca o preparador precisa
            abrir modalidade por modalidade para saber qual falta. */}
        {tipo === "fisico" && (dados.modalidades || []).length > 1 && (
          <>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
              {(dados.modalidades || []).map((m) => {
                const chave = (x) => String(x || "").normalize("NFD").replace(DIACRITICOS, "").toLowerCase().replace(/[^a-z0-9]/g, "");
                const doPlano = (dados.planos || []).find((p) => p.tipo === "fisico" && chave(p.modalidade) === chave(m));
                const est = doPlano ? doPlano.status : null;
                const marca = est === "aprovado" ? "✓" : est === "enviado" ? "•" : est === "devolvido" ? "!" : null;
                const corDaMarca = est === "aprovado" ? OK : est === "devolvido" ? RUIM : ATENCAO;
                const ativa = m === modalidadeAtiva;
                return (
                  <button key={m} onClick={() => setModalidadeAtiva(m)} style={{
                    background: ativa ? AZUL : BRANCO,
                    color: ativa ? BRANCO : CINZA_ESC,
                    border: `1.5px solid ${ativa ? AZUL : "#E5E7EB"}`,
                    borderRadius: 9, padding: "7px 14px", fontSize: 12.5, fontWeight: 700,
                    cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                  }}>
                    {m}
                    {marca && <span style={{ color: ativa ? BRANCO : corDaMarca, fontSize: 12 }}>{marca}</span>}
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 11.5, color: CINZA_ESC, marginTop: 7, lineHeight: 1.5 }}>
              Um plano por modalidade. <b>✓</b> aprovado, <b>•</b> enviado, <b>!</b> devolvido;
              sem marca, ainda em rascunho.
            </div>
          </>
        )}

        {bloqueado && (
          <div style={{ background: AZUL_CLARO, border: "1.5px solid #BFD0EC", borderRadius: 10, padding: "12px 15px", fontSize: 12.5, color: AZUL_ESCURO, lineHeight: 1.6, marginTop: 14 }}>
            {plano.status === "aprovado"
              ? "Plano aprovado. Para ajustar, peça à coordenação para devolver."
              : "Plano enviado e em análise pela coordenação. Enquanto isso, ele fica somente leitura."}
          </div>
        )}
      </Secao>

      {/* 2. O que orienta o trimestre inteiro, incluindo as metas
          individuais: objetivo de atleta é meta, e meta mora na direção. No
          calendário ela ficaria ao lado de datas, que é outra conversa. */}
      <Secao numero={2} titulo="Direção do trimestre">
        <Rotulo>Objetivo geral do trimestre</Rotulo>
        <textarea rows={3} disabled={somenteLeitura} value={conteudo.objetivoGeral}
          onChange={(e) => mudar("objetivoGeral", e.target.value)}
          placeholder="O que a turma precisa alcançar neste trimestre."
          style={estiloCampo} />

        <div style={{ height: 16 }} />
        <Rotulo>Volume semanal previsto</Rotulo>
        <input disabled={somenteLeitura} value={conteudo.volumeSemanal}
          onChange={(e) => mudar("volumeSemanal", e.target.value)}
          placeholder="Ex: 4 sessões por semana, 90 minutos cada" style={estiloCampo} />

        <div style={{ height: 20 }} />
        <Rotulo>Objetivos de atletas específicos</Rotulo>
        <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 9, lineHeight: 1.5 }}>
          Opcional. Use para metas individuais que não valem para a turma inteira.
        </div>
        {(conteudo.objetivosPorAtleta || []).map((o, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "minmax(150px, 1fr) 2fr auto", gap: 9, marginBottom: 8, alignItems: "center" }}>
            {atletas.length ? (
              <select disabled={somenteLeitura} value={o.atletaUid || ""}
                onChange={(e) => {
                  const a = atletas.find((x) => x.uid === e.target.value);
                  mudarLista("objetivosPorAtleta", i, {
                    atletaUid: a ? a.uid : "", atletaNome: a ? a.nome : "",
                  });
                }}
                style={{ ...estiloCampo, cursor: "pointer" }}>
                <option value="">— escolha o atleta —</option>
                {atletas
                  .filter((a) => a.uid === o.atletaUid || !jaEscolhidos.has(a.uid))
                  .map((a) => <option key={a.uid} value={a.uid}>{a.nome}</option>)}
              </select>
            ) : (
              <input disabled value={o.atletaNome || ""} style={{ ...estiloCampo, background: FUNDO_SUAVE }} />
            )}
            <input disabled={somenteLeitura} value={o.objetivo || ""} placeholder="Objetivo dele no trimestre"
              onChange={(e) => mudarLista("objetivosPorAtleta", i, { objetivo: e.target.value })} style={estiloCampo} />
            {!somenteLeitura && (
              <button onClick={() => remover("objetivosPorAtleta", i)}
                style={{ background: "none", border: "none", color: PERIGO, fontSize: 14, cursor: "pointer" }}>✕</button>
            )}
          </div>
        ))}
        {!somenteLeitura && atletas.length > jaEscolhidos.size && (
          <button onClick={() => adicionar("objetivosPorAtleta", { atletaUid: "", atletaNome: "", objetivo: "" })}
            style={{ background: "none", border: "none", color: AZUL, fontSize: 12.5, fontWeight: 700, cursor: "pointer", padding: 0 }}>
            + adicionar atleta
          </button>
        )}
      </Secao>

      {/* 3. Os três meses, um de cada vez.
          Lado a lado, os três disputavam a largura e o microciclo ficava
          espremido em cinco campinhos. Um mês aberto por vez devolve a
          largura da página para a semana, que é o que se lê de relance. */}
      <Secao numero={3} titulo="Planejamento mensal">
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {conteudo.meses.map((m, i) => {
            const aberto = mesAberto === i;
            const situacao = situacaoDoMes(m);
            return (
              <div key={i} style={{ border: `1.5px solid ${aberto ? AZUL : BORDA}`, borderRadius: 11, overflow: "hidden" }}>
                <button onClick={() => setMesAberto(aberto ? -1 : i)} style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 10,
                  background: aberto ? AZUL_CLARO : BRANCO, border: "none",
                  padding: "12px 15px", cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                }}>
                  <span style={{ fontSize: 11, color: CINZA_ESC }}>{aberto ? "▾" : "▸"}</span>
                  <span style={{ fontSize: 14.5, fontWeight: 800, color: aberto ? AZUL_ESCURO : TEXTO, flex: 1 }}>
                    {m.rotulo || meses[i]}
                  </span>
                  <span style={{
                    fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                    background: situacao.bg, color: situacao.cor, whiteSpace: "nowrap",
                  }}>
                    {situacao.txt}
                  </span>
                </button>

                {aberto && (
                  <div style={{ padding: "14px 15px", borderTop: `1px solid ${BORDA}` }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
                      <div>
                        <Rotulo>Objetivo do mês</Rotulo>
                        <input disabled={somenteLeitura} value={m.foco || ""} placeholder="O que o mês persegue"
                          onChange={(e) => mudarMes(i, "foco", e.target.value)} style={estiloCampo} />
                      </div>
                      <div>
                        <Rotulo>Conteúdos principais</Rotulo>
                        <input disabled={somenteLeitura} value={m.conteudos || ""} placeholder="O que será trabalhado"
                          onChange={(e) => mudarMes(i, "conteudos", e.target.value)} style={estiloCampo} />
                      </div>
                    </div>

                    {/* O microciclo ocupa a largura toda: a semana se lê de
                        uma vez, e cada dia cresce conforme se escreve.
                        Com mais de uma semana, cada uma ganha seu título —
                        com uma só, o título seria ruído. */}
                    {(() => {
                      const semanas = semanasDoMes(m);
                      const varias = semanas.length > 1;
                      return (
                        <div style={{ marginTop: 16 }}>
                          <Rotulo>{varias ? "Microciclo do mês" : "Microciclo da semana"}</Rotulo>
                          {semanas.map((semana, sem) => (
                            <div key={sem} style={{ marginTop: sem ? 14 : 0 }}>
                              {varias && (
                                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                                  <span style={{ fontSize: 12, fontWeight: 800, color: AZUL_ESCURO }}>
                                    Semana {sem + 1}
                                  </span>
                                  <div style={{ flex: 1, borderTop: `1px solid ${BORDA}` }} />
                                  {!somenteLeitura && (
                                    <button onClick={() => removerSemana(i, sem)} style={{
                                      background: "none", border: "none", padding: "2px 4px", cursor: "pointer",
                                      fontSize: 12, fontWeight: 600, color: CINZA_ESC, fontFamily: "inherit",
                                    }}>
                                      Remover
                                    </button>
                                  )}
                                </div>
                              )}
                              <div style={{ display: "grid", gridTemplateColumns: `repeat(${DIAS_DO_MICROCICLO.length}, 1fr)`, gap: 9 }}>
                                {DIAS_DO_MICROCICLO.map((d) => (
                                  <div key={d.chave}>
                                    <div style={{ fontSize: 11.5, fontWeight: 700, color: AZUL_ESCURO, marginBottom: 5 }}>
                                      {d.rotulo}
                                    </div>
                                    <CampoQueCresce disabled={somenteLeitura}
                                      value={semana[d.chave] || ""}
                                      onChange={(v) => mudarDiaDoMicrociclo(i, sem, d.chave, v)}
                                      placeholder="O que se faz neste dia…"
                                      minimoDeLinhas={4} />
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}

                          {!somenteLeitura && (
                            <div style={{ marginTop: 12 }}>
                              {semanas.length < MAXIMO_DE_SEMANAS ? (
                                <button onClick={() => adicionarSemana(i)} style={{
                                  background: "none", border: `1px solid ${AZUL}`, borderRadius: 8,
                                  padding: "7px 13px", fontSize: 12.5, fontWeight: 700, color: AZUL,
                                  cursor: "pointer", fontFamily: "inherit",
                                }}>
                                  + Adicionar semana
                                </button>
                              ) : (
                                <div style={{ fontSize: 11.5, color: CINZA_ESC }}>
                                  Cinco semanas é o máximo de um mês.
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {!somenteLeitura && conteudo.meses.length > 1 && (
                      <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {conteudo.meses.map((_, k) => k !== i && (
                          <button key={k} onClick={() => copiarMesPara(i, k)} style={{
                            background: "none", border: `1px solid ${BORDA}`, borderRadius: 8,
                            padding: "6px 11px", fontSize: 12, fontWeight: 600, color: TEXTO,
                            cursor: "pointer", fontFamily: "inherit",
                          }}>
                            Copiar para {meses[k]}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Secao>

      {/* 4. As datas do trimestre: as provas e a janela de avaliação. O
          técnico cadastra as competições; o preparador lê as dele. */}
      <Secao numero={4} titulo="Calendário de competições e avaliações"
        sub={tipo === "fisico"
          ? `Provas cadastradas pelo técnico de ${modalidadeAtiva || "cada modalidade"} e a janela de avaliação do trimestre.`
          : "As provas do trimestre e a janela de avaliação definida pela coordenação."}>

        <Rotulo>Competições previstas</Rotulo>
        {tipo === "fisico" ? (
          competicoesDoTecnico.length === 0 ? (
            <div style={{ fontSize: 12.5, color: CINZA_ESC }}>
              O técnico ainda não cadastrou competição para este trimestre.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {competicoesDoTecnico.map((c, i) => (
                <div key={i} style={{
                  border: `1px solid ${BORDA}`, borderRadius: 10, padding: "10px 13px",
                  display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap",
                }}>
                  <span style={{ fontSize: 12.5, color: CINZA_ESC, minWidth: 110 }}>
                    {periodoDaCompeticao(c) || "sem data"}
                  </span>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: TEXTO, flex: 1, minWidth: 160 }}>
                    {c.nome || "(sem nome)"}
                  </span>
                  <span style={{ fontSize: 12, color: CINZA_ESC }}>
                    {[c.local, (c.categorias || []).join(", ")].filter(Boolean).join(" · ")}
                  </span>
                </div>
              ))}
            </div>
          )
        ) : (
          <>
            {(conteudo.competicoes || []).length === 0 && (
              <div style={{ fontSize: 12.5, color: CINZA_ESC, marginBottom: 10 }}>
                Nenhuma competição cadastrada ainda.
              </div>
            )}
            {(conteudo.competicoes || []).map((c, i) => (
              <div key={i} style={{ border: "1.5px solid #E5E7EB", borderRadius: 11, padding: "13px 15px", marginBottom: 9, background: "#FCFDFF" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
                  <div>
                    <Rotulo>Início</Rotulo>
                    <input type="date" disabled={somenteLeitura} value={c.dataInicio || ""}
                      onChange={(e) => mudarLista("competicoes", i, { dataInicio: e.target.value })} style={estiloCampo} />
                  </div>
                  <div>
                    <Rotulo>Fim (se durar mais de um dia)</Rotulo>
                    <input type="date" disabled={somenteLeitura} value={c.dataFim || ""}
                      onChange={(e) => mudarLista("competicoes", i, { dataFim: e.target.value })} style={estiloCampo} />
                  </div>
                  <div>
                    <Rotulo>Competição</Rotulo>
                    <input disabled={somenteLeitura} value={c.nome || ""} placeholder="Nome da prova"
                      onChange={(e) => mudarLista("competicoes", i, { nome: e.target.value })} style={estiloCampo} />
                  </div>
                  <div>
                    <Rotulo>Local</Rotulo>
                    <input disabled={somenteLeitura} value={c.local || ""} placeholder="Cidade, UF"
                      onChange={(e) => mudarLista("competicoes", i, { local: e.target.value })} style={estiloCampo} />
                  </div>
                </div>

                <div style={{ marginTop: 11, display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: CINZA_ESC, fontWeight: 700, textTransform: "uppercase" }}>Categorias</span>
                  {(c.categorias || []).map((cat, k) => (
                    <span key={k} style={{ background: BRANCO, border: "1.5px solid #CBD5E1", borderRadius: 20, padding: "4px 10px", fontSize: 12, fontWeight: 700 }}>
                      {cat}
                      {!somenteLeitura && (
                        <button onClick={() => mudarLista("competicoes", i, { categorias: c.categorias.filter((_, j) => j !== k) })}
                          style={{ background: "none", border: "none", color: "#94A3B8", cursor: "pointer", marginLeft: 6, padding: 0 }}>✕</button>
                      )}
                    </span>
                  ))}
                  {!somenteLeitura && (
                    categorias.length ? (
                      <select value="" onChange={(e) => {
                        const nova = e.target.value;
                        if (!nova) return;
                        if ((c.categorias || []).includes(nova)) return;
                        mudarLista("competicoes", i, { categorias: [...(c.categorias || []), nova] });
                      }} style={{ ...estiloCampo, width: "auto", minWidth: 160, fontSize: 12.5, padding: "6px 9px", cursor: "pointer" }}>
                        <option value="">+ categoria…</option>
                        {categorias
                          .filter((cat) => !(c.categorias || []).includes(cat))
                          .map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                      </select>
                    ) : (
                      <span style={{ fontSize: 11.5, color: CINZA_ESC }}>
                        Nenhuma categoria cadastrada nos atletas ainda. A coordenação preenche em Gerenciar usuários.
                      </span>
                    )
                  )}
                </div>

                {periodoDaCompeticao(c) && (
                  <div style={{ fontSize: 11.5, color: CINZA_ESC, marginTop: 9 }}>
                    Vai aparecer como <b>{periodoDaCompeticao(c)}</b>
                  </div>
                )}

                {!somenteLeitura && (
                  <div style={{ marginTop: 10 }}>
                    <button onClick={() => remover("competicoes", i)}
                      style={{ background: "none", border: "none", color: PERIGO, fontSize: 12.5, fontWeight: 700, cursor: "pointer", padding: 0 }}>
                      Remover competição
                    </button>
                  </div>
                )}
              </div>
            ))}
            {!somenteLeitura && (
              <button onClick={() => adicionar("competicoes", { dataInicio: "", dataFim: "", nome: "", local: "", categorias: [] })}
                style={{ background: "none", border: "none", color: AZUL, fontSize: 12.5, fontWeight: 700, cursor: "pointer", padding: 0 }}>
                + adicionar competição
              </button>
            )}
          </>
        )}

        <div style={{ borderTop: `1px solid ${BORDA}`, marginTop: 20, paddingTop: 16 }}>
          <Rotulo>{tipo === "fisico" ? "Avaliação e testes físicos" : "Avaliação técnica"}</Rotulo>
          <div style={{ background: AZUL_CLARO, border: "1.5px solid #BFD0EC", borderRadius: 8, padding: "10px 13px", marginBottom: 14 }}>
            {trimestre.avaliacaoInicio ? (
              <>
                <div style={{ fontSize: 13, fontWeight: 800, color: AZUL_ESCURO }}>
                  {fmt(trimestre.avaliacaoInicio)} a {fmt(trimestre.avaliacaoFim)}
                </div>
                <div style={{ fontSize: 11.5, color: AZUL_ESCURO, marginTop: 3, lineHeight: 1.5 }}>
                  Janela definida pela coordenação. {avaliacaoDeQuem}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 12, color: AZUL_ESCURO, lineHeight: 1.5 }}>
                A coordenação ainda não definiu a janela de avaliações deste trimestre.
              </div>
            )}
          </div>

          <Rotulo>O que será avaliado na janela</Rotulo>
          <textarea rows={2} disabled={somenteLeitura} value={conteudo.avaliacaoConteudo}
            onChange={(e) => mudar("avaliacaoConteudo", e.target.value)}
            placeholder="Testes, protocolos e critérios que serão aplicados." style={estiloCampo} />
        </div>
      </Secao>

      {/* 5. Revisão e envio. O último passo mostra o que falta e o que
          acontece depois de clicar. */}
      <Secao numero={5} titulo="Revisão e envio">
        <Rotulo>Observações</Rotulo>
        <textarea rows={2} disabled={somenteLeitura} value={conteudo.observacoes}
          onChange={(e) => mudar("observacoes", e.target.value)} placeholder="Opcional…" style={estiloCampo} />

        {!somenteLeitura && (
          <>
            {faltas.length > 0 ? (
              <div style={{
                marginTop: 16, background: ATENCAO_CL, border: `1px solid ${ATENCAO}33`,
                borderRadius: 10, padding: "11px 14px",
              }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: ATENCAO }}>
                  Falta preencher antes de enviar
                </div>
                <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 12.5, color: ATENCAO, lineHeight: 1.7 }}>
                  {faltas.map((f) => <li key={f}>{f}</li>)}
                </ul>
              </div>
            ) : (
              <div style={{
                marginTop: 16, background: OK_CL, border: `1px solid ${OK}33`,
                borderRadius: 10, padding: "11px 14px", fontSize: 12.5, color: OK, fontWeight: 700,
              }}>
                Tudo que é obrigatório está preenchido.
              </div>
            )}

            {/* Salvar e enviar são coisas diferentes, e por isso são dois
                botões. O plano é escrito em pedaços ao longo de dias: dá
                para salvar pela metade quantas vezes precisar, e só enviar
                quando estiver pronto, dentro do prazo. Enviar é o que
                trava para edição. */}
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 16 }}>
              <Btn onClick={salvarAgora} disabled={salvando} variant="secondary">
                {salvando ? "Salvando…" : "Salvar rascunho"}
              </Btn>
              <Btn onClick={enviar} disabled={enviando || faltas.length > 0}>
                {enviando ? "Enviando…" : "Enviar para aprovação"}
              </Btn>
              <span style={{ fontSize: 12, color: salvando ? AZUL : CINZA_ESC }}>
                {salvando
                  ? "Salvando…"
                  : salvoEm
                    ? `Rascunho salvo às ${salvoEm.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`
                    : plano && plano.atualizadoEm
                      ? `Salvo em ${dataHoraCurta(plano.atualizadoEm)}${plano.atualizadoPorNome ? " por " + plano.atualizadoPorNome : ""}`
                      : "Salva sozinho enquanto você digita; o botão grava na hora."}
              </span>
            </div>
          </>
        )}
      </Secao>
    </div>
  );
}
